interface DebugSectionsProps {
  formData: Record<string, any>;
  falRawOutput: any;
}

export const DebugSections = ({ formData, falRawOutput }: DebugSectionsProps) => {
  return (
    <div className="space-y-4">
      {/* Form Data Debug */}
      <details className="mt-4">
        <summary className="text-sm text-th-secondary cursor-pointer hover:text-th-primary flex items-center space-x-2">
          <span>🔍 View Current Form Data ({Object.keys(formData).length} fields)</span>
          <span className="text-xs px-2 py-0.5 bg-elevated border border-subtle rounded">
            Internal State
          </span>
        </summary>
        <pre className="mt-2 p-4 bg-surface border border-subtle rounded-lg text-xs text-th-secondary overflow-x-auto">
          {JSON.stringify(formData, null, 2)}
        </pre>
      </details>

      {/* FAL Raw Output Debug */}
      {falRawOutput && (
        <details className="mt-4">
          <summary className="text-sm text-th-secondary cursor-pointer hover:text-th-primary flex items-center space-x-2">
            <span>📥 View FAL Raw JSON Output</span>
            <span className="text-xs px-2 py-0.5 bg-accent/20 text-accent rounded">
              {Object.keys(falRawOutput).length} keys
            </span>
            <span className="text-xs px-2 py-0.5 bg-status-success/20 text-status-success rounded">
              API Response
            </span>
          </summary>
          <div className="mt-2 p-4 bg-overlay border border-subtle rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-2 h-2 bg-muted rounded-full"></div>
              <span className="text-th-primary font-medium text-sm">Latest FAL API Response</span>
              <span className="text-xs text-th-secondary">
                {new Date().toLocaleTimeString()}
              </span>
            </div>
            
            {/* Output Path Analysis */}
            <div className="mb-4 p-3 bg-status-warning/10 border border-status-warning/30 rounded">
              <h6 className="text-status-warning font-medium text-sm mb-2">🔍 Output Path Analysis</h6>
              <div className="space-y-2 text-xs">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <span className="text-th-secondary">Root keys:</span>
                    <code className="text-status-warning font-mono ml-2">
                      {falRawOutput ? Object.keys(falRawOutput).join(', ') : 'none'}
                    </code>
                  </div>
                  <div>
                    <span className="text-th-secondary">Output keys:</span>
                    <code className="text-status-warning font-mono ml-2">
                      {falRawOutput?.output ? Object.keys(falRawOutput.output).join(', ') : 'none'}
                    </code>
                  </div>
                </div>
                
                {falRawOutput?.output?.images && (
                  <div>
                    <span className="text-th-secondary">Images found:</span>
                    <code className="text-status-success font-mono ml-2">
                      Array({falRawOutput.output.images.length}) - First URL: {falRawOutput.output.images[0]?.url ? '✅' : '❌'}
                    </code>
                  </div>
                )}
                
                <div className="pt-2 border-t border-status-warning/20">
                  <span className="text-th-secondary">Complete path:</span>
                  <code className="text-status-warning font-mono ml-2">
                    result.output.images[0].url
                  </code>
                </div>
              </div>
            </div>
            
            {/* Media Preview Section */}
            {falRawOutput?.output && (
              <div className="mt-4 p-3 bg-brand/10 border border-brand/30 rounded">
                <h6 className="text-brand font-medium text-sm mb-3">🖼️ Media Preview</h6>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* Preview Images */}
                  {falRawOutput.output.images && Array.isArray(falRawOutput.output.images) && 
                    falRawOutput.output.images.slice(0, 6).map((image: any, index: number) => (
                      <div key={index} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden bg-elevated">
                          <img
                            src={image.url || image}
                            alt={`Generated image ${index + 1}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                            onClick={() => window.open(image.url || image, '_blank')}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = '<div class="w-full h-full flex items-center justify-center text-th-secondary"><svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg></div>';
                              }
                            }}
                          />
                          {/* Image overlay with index */}
                          <div className="absolute top-2 left-2">
                            <span className="bg-brand text-surface text-xs px-2 py-1 rounded-full font-medium">
                              {index + 1}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  
                  {/* Preview Single Image */}
                  {falRawOutput.output.image && (
                    <div className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden bg-elevated">
                        <img
                          src={falRawOutput.output.image.url || falRawOutput.output.image}
                          alt="Generated image"
                          className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                          onClick={() => window.open(falRawOutput.output.image.url || falRawOutput.output.image, '_blank')}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="w-full h-full flex items-center justify-center text-th-secondary"><svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg></div>';
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Preview Videos */}
                  {falRawOutput.output.videos && Array.isArray(falRawOutput.output.videos) && 
                    falRawOutput.output.videos.slice(0, 6).map((video: any, index: number) => (
                      <div key={index} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden bg-elevated">
                          <video
                            src={video.url || video}
                            className="w-full h-full object-cover"
                            controls
                            preload="metadata"
                            onError={(e) => {
                              const target = e.target as HTMLVideoElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = '<div class="w-full h-full flex items-center justify-center text-th-secondary"><svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg></div>';
                              }
                            }}
                          />
                          {/* Video overlay with index */}
                          <div className="absolute top-2 left-2">
                            <span className="bg-brand text-surface text-xs px-2 py-1 rounded-full font-medium">
                              Video {index + 1}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}

                  {/* Preview Single Video */}
                  {falRawOutput.output.video && (
                    <div className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden bg-elevated">
                        <video
                          src={falRawOutput.output.video.url || falRawOutput.output.video}
                          className="w-full h-full object-cover"
                          controls
                          preload="metadata"
                          onError={(e) => {
                            const target = e.target as HTMLVideoElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="w-full h-full flex items-center justify-center text-th-secondary"><svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg></div>';
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Preview Audio */}
                  {falRawOutput.output.audios && Array.isArray(falRawOutput.output.audios) && 
                    falRawOutput.output.audios.slice(0, 6).map((audio: any, index: number) => (
                      <div key={index} className="relative group">
                        <div className="aspect-square rounded-lg bg-gradient-to-br from-muted/30 to-overlay flex flex-col items-center justify-center p-4">
                          <svg className="w-12 h-12 text-th-secondary mb-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                          </svg>
                          <audio
                            src={audio.url || audio}
                            controls
                            className="w-full"
                            preload="metadata"
                          />
                          <div className="absolute top-2 left-2">
                            <span className="bg-brand text-surface text-xs px-2 py-1 rounded-full font-medium">
                              Audio {index + 1}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  
                  {/* Preview Single Audio */}
                  {falRawOutput.output.audio && (
                    <div className="relative group">
                      <div className="aspect-square rounded-lg bg-gradient-to-br from-muted/30 to-overlay flex flex-col items-center justify-center p-4">
                        <svg className="w-12 h-12 text-th-secondary mb-3" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                        </svg>
                        <audio
                          src={falRawOutput.output.audio.url || falRawOutput.output.audio}
                          controls
                          className="w-full"
                          preload="metadata"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* No media found message */}
                  {!falRawOutput.output.images && !falRawOutput.output.image && 
                   !falRawOutput.output.videos && !falRawOutput.output.video &&
                   !falRawOutput.output.audios && !falRawOutput.output.audio && (
                    <div className="col-span-full text-center py-8">
                      <div className="text-th-secondary">
                        <svg className="w-12 h-12 mx-auto mb-3" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                        </svg>
                        <p className="text-sm">No media found in output</p>
                        <p className="text-xs mt-1">
                          Available keys: {Object.keys(falRawOutput.output || {}).join(', ') || 'none'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <pre className="text-xs text-th-secondary overflow-x-auto bg-surface p-3 rounded border border-subtle font-mono">
              {JSON.stringify(falRawOutput, null, 2)}
            </pre>
          </div>
        </details>
      )}
    </div>
  );
};